import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { IconTrash } from '@tabler/icons-react';
import {
  removeProductOrProjectFromFavorites,
  fetchListOfFavorites,
  removeDeletedFavItem
} from '../../redux/slices/client/clientProductSlice';

import EmptyState from '../../components/common/EmptySpace';

const ShimmerItem = () => {
  return (
    <div className="bg-white rounded-md p-4 shadow-sm flex flex-col items-center relative h-full">
      <div className="w-full h-48 mb-3 relative bg-gray-200 animate-pulse rounded-md"></div>
      <div className="w-full space-y-2">
        <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3 mx-auto"></div>
        <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3 mx-auto"></div>
        <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4 mx-auto"></div>
        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2 mx-auto"></div>
      </div>
      <div className="mt-4 h-8 bg-gray-200 rounded-md animate-pulse w-3/4"></div>
    </div>
  );
};

const ItemInfoDisplay = ({ item }) => {

  const typeDisplay = item.type === 'project' ? 'Project' : 'Product';

  // Get names
  const primaryName = item.type === 'project' ? item.name : item.product_name;
  const secondaryName = item.type === 'project' ? item.product?.product_name : null;


  const category = item.product?.category?.name || item.category?.name || 'Uncategorized';
  const subcategory = item.product?.sub_category?.name || item.sub_category?.name || 'No subcategory';


  const priceDisplay = item.price ? `From $${item.price}` : 'Price not available';

  return (
    <div className="w-full text-center space-y-1">

      <span className={`inline-block px-2 py-1 text-xs rounded-full mb-1 ${item.type === 'project'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-purple-100 text-purple-800'
        }`}>
        {typeDisplay}
      </span>


      <p className="font-semibold text-base truncate">
        {primaryName}
      </p>


      {secondaryName && (
        <p className="text-sm text-gray-600 truncate">
          {secondaryName}
        </p>
      )}


      <p className="text-xs text-gray-500">
        {category} • {subcategory}
      </p>


      <p className="text-sm text-gray-600 font-medium mt-1">
        {priceDisplay}
      </p>
    </div>
  );
};

export default function Favorite() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const {
    errorListFavorites,
    loadingListOfFavorites,
    ListOfFavorites
  } = useSelector((state) => state.productByCategory);

  const [flippedItems, setFlippedItems] = useState({});


  useEffect(() => {
    dispatch(fetchListOfFavorites());
  }, [dispatch]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = ListOfFavorites?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = Math.ceil((ListOfFavorites?.length || 0) / itemsPerPage);



  const toggleFlip = (itemId) => {
    setFlippedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleRemoveFavorite = (itemId) => {
    dispatch(removeProductOrProjectFromFavorites(itemId))
      .then(() => {
        dispatch(removeDeletedFavItem(itemId));
      });
  };

  const handleCustomize = (item) => {
    if (item.type === 'project') {
      navigate(`/design/${item.product.sub_category.id}/false/${item.id}`);
    } else {
      navigate(`/design/${item.sub_category.id}/true`);
    }
  };

  const reconstructLayers = (design, containerWidth, containerHeight) => {
    const originalDesignWidth = 500;
    const originalDesignHeight = 400;
    const scaleX = containerWidth / originalDesignWidth;
    const scaleY = containerHeight / originalDesignHeight;

    const processLayer = (layer) => {
      const baseLayer = {
        id: layer.id,
        type: layer.type,
        x: layer.x * scaleX,
        y: layer.y * scaleY,
        width: layer.width * scaleX,
        height: layer.height * scaleY,
        rotation: layer.rotation,
        flipped: layer.flipped,
        visible: layer.visible,
        zIndex: layer.z_index,
        borderColor: layer.border_color,
        borderStyle: layer.border_style,
        offsetX: layer.offset_x * scaleX,
        offsetY: layer.offset_y * scaleY,
      };

      if (layer.type === 'image' && layer.image_data) {
        return {
          ...baseLayer,
          src: layer.image_data.original_src || layer.image_data.src || '',
        };
      } else if (layer.type === 'text' && layer.text_data) {
        return {
          ...baseLayer,
          text: layer.text_data.text,
          color: layer.text_data.color,
        };
      }
      return baseLayer;
    };

    const frontLayers = design.layers
      .filter(l => l.side === 'front')
      .map(processLayer);

    const backLayers = design.layers
      .filter(l => l.side === 'back')
      .map(processLayer);

    return { frontLayers, backLayers };
  };

  const renderProjectPreview = (project) => {
    const previewWidth = 300;
    const previewHeight = 250;
    const isFlipped = flippedItems[project.id];
    const { frontLayers, backLayers } = reconstructLayers(project, previewWidth, previewHeight);
    const frontImage = project.product?.images?.front_image || '';
    const backImage = project.product?.images?.back_image || frontImage;

    return (
      <div className="relative w-full h-full bg-gray-100 rounded-md overflow-hidden">
        <div
          className={`flipper ${isFlipped ? 'flipped' : ''}`}
          onClick={() => toggleFlip(project.id)}
        >
          {/* Front View */}
          <div className="front">
            {frontImage && (
              <img
                src={frontImage}
                alt="Product front"
                className="absolute w-full h-full object-contain"
                style={{ zIndex: 1 }}
              />
            )}
            {frontLayers.map((layer) => (
              layer.visible && (
                <div
                  key={layer.id}
                  className="absolute"
                  style={{
                    top: layer.y,
                    left: layer.x,
                    width: layer.width,
                    height: layer.height,
                    transform: `rotate(${layer.rotation}deg) scaleX(${layer.flipped ? -1 : 1})`,
                    zIndex: layer.zIndex + 2,
                    borderTopWidth: layer.border?.top || 0,
                    borderRightWidth: layer.border?.right || 0,
                    borderBottomWidth: layer.border?.bottom || 0,
                    borderLeftWidth: layer.border?.left || 0,
                    borderColor: layer.borderColor,
                    borderStyle: layer.borderStyle,
                    boxShadow: `${layer.offsetX}px ${layer.offsetY}px 8px rgba(0,0,0,0.2)`,
                  }}
                >
                  {layer.type === 'text' ? (
                    <div
                      className="w-full h-full flex items-center justify-center p-1"
                      style={{ color: layer.color }}
                    >
                      {layer.text}
                    </div>
                  ) : (
                    layer.src && (
                      <img
                        src={layer.src}
                        alt="Layer"
                        className="w-full h-full object-cover"
                      />
                    )
                  )}
                </div>
              )
            ))}
          </div>

          {/* Back View */}
          <div className="back">
            {backImage && (
              <img
                src={backImage}
                alt="Product back"
                className="absolute w-full h-full object-contain"
                style={{ zIndex: 1 }}
              />
            )}
            {backLayers.map((layer) => (
              layer.visible && (
                <div
                  key={layer.id}
                  className="absolute"
                  style={{
                    top: layer.y,
                    left: layer.x,
                    width: layer.width,
                    height: layer.height,
                    transform: `rotate(${layer.rotation}deg) scaleX(${layer.flipped ? -1 : 1})`,
                    zIndex: layer.zIndex + 2,
                    borderTopWidth: layer.border?.top || 0,
                    borderRightWidth: layer.border?.right || 0,
                    borderBottomWidth: layer.border?.bottom || 0,
                    borderLeftWidth: layer.border?.left || 0,
                    borderColor: layer.borderColor,
                    borderStyle: layer.borderStyle,
                    boxShadow: `${layer.offsetX}px ${layer.offsetY}px 8px rgba(0,0,0,0.2)`,
                  }}
                >
                  {layer.type === 'text' ? (
                    <div
                      className="w-full h-full flex items-center justify-center p-1"
                      style={{ color: layer.color }}
                    >
                      {layer.text}
                    </div>
                  ) : (
                    layer.src && (
                      <img
                        src={layer.src}
                        alt="Layer"
                        className="w-full h-full object-cover"
                      />
                    )
                  )}
                </div>
              )
            ))}
          </div>
        </div>

        {/* Delete button */}
        <div className="absolute top-2 right-2 z-[1000]">
          <button
            className="bg-white border border-gray-300 p-1 rounded-md shadow-sm"
            title="Remove from favorites"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveFavorite(project.id);
            }}
          >
            <IconTrash size={16} className="text-gray-500 hover:text-red-500" />
          </button>
        </div>
      </div>
    );
  };

  const renderProductPreview = (product) => {
    const isFlipped = flippedItems[product.id];
    const frontImage = product.template?.media_files?.[0]?.front_image || product.images?.front_image;
    const backImage = product.template?.media_files?.[0]?.back_image || product.images?.back_image || frontImage;

    return (
      <div className="relative w-full h-full bg-gray-100 rounded-md overflow-hidden">
        <div
          className={`flipper ${isFlipped ? 'flipped' : ''}`}
          onClick={() => toggleFlip(product.id)}
        >
          {/* Front View */}
          <div className="front">
            {frontImage ? (
              <img
                src={frontImage}
                alt={product.product_name}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-gray-500">No preview available</p>
              </div>
            )}
          </div>

          {/* Back View */}
          <div className="back">
            {backImage ? (
              <img
                src={backImage}
                alt={`${product.product_name} (back)`}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-gray-500">No preview available</p>
              </div>
            )}
          </div>
        </div>

        {/* Delete button */}
        <div className="absolute top-2 right-2 z-[1000]">
          <button
            className="bg-white border border-gray-300 p-1 rounded-md shadow-sm"
            title="Remove from favorites"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveFavorite(product.id);
            }}
          >
            <IconTrash size={16} className="text-gray-500 hover:text-red-500" />
          </button>
        </div>
      </div>
    );
  };

  const Pagination = () => {
    // Don't show pagination if there's only one page
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center mt-8">
        <nav className="flex items-center gap-1">
          {/* Previous Button */}
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-md border text-sm ${currentPage === 1
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
          >
            ‹ Prev
          </button>

          {/* Page Numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNumber;
            if (totalPages <= 5) {
              pageNumber = i + 1;
            } else if (currentPage <= 3) {
              pageNumber = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNumber = totalPages - 4 + i;
            } else {
              pageNumber = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                className={`px-3 py-1 rounded-md border text-sm ${currentPage === pageNumber
                    ? 'bg-[#00148c] text-white border-[#00148c]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
              >
                {pageNumber}
              </button>
            );
          })}

          {/* Next Button */}
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-md border text-sm ${currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
          >
            Next ›
          </button>
        </nav>
      </div>
    );
  };

  if (loadingListOfFavorites) {
    return (
      <div className="bg-[#EEE8E8] min-h-screen py-8">
        <div className="max-w-screen-xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-[#00148c] mb-8">Loading favorites...</h1>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <ShimmerItem key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (errorListFavorites) {
    return (
      <div className="bg-[#EEE8E8] min-h-screen py-8">
        <div className="max-w-screen-xl mx-auto px-4">
          <h1 className="text-3xl font-bold text-[#00148c] mb-8">Error loading favorites</h1>
          <p className="text-red-500">{errorListFavorites}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#EEE8E8] min-h-screen py-8">
      <div className="max-w-screen-xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-[#00148c] mb-8">My Favorites</h1>

        { (!loadingListOfFavorites && ListOfFavorites?.length === 0) && (
          <EmptyState
            title="No Favorites Found"
            description="You haven't added any items to your favorites yet."
            icon="favorites"
            showButton={true}
            buttonText="check out our Product Categories"
            buttonLink="/"
          />
        )}

        {/* Favorites List */}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 mb-4">
          {loadingListOfFavorites ? (
            [...Array(8)].map((_, i) => (
              <ShimmerItem key={i} />
            ))
          ) : currentItems?.map((item) => {
            const isOutOfStock = item.type === 'project'
              ? item.project?.stock <= 0
              : item.stock <= 0;

            return (
              <div
                key={item.favorite_id}
                className="bg-white rounded-md p-4 shadow-sm flex flex-col items-center relative hover:shadow-md transition-shadow"
              >
                <div className="w-full h-48 mb-3 relative">
                  {item.type === 'project'
                    ? renderProjectPreview(item)
                    : renderProductPreview(item)
                  }
                </div>

                <ItemInfoDisplay item={item} />

                <button
                  className={`font-semibold text-sm px-3 py-1 rounded-md mt-auto ${
                    isOutOfStock
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#FFD100] hover:bg-[#FFC300] text-[#00148c]'
                  }`}
                  onClick={() => !isOutOfStock && handleCustomize(item)}
                  disabled={isOutOfStock}
                >
                  {isOutOfStock
                    ? 'Out of Stock'
                    : item.type === 'project'
                      ? 'Edit Project'
                      : 'Customize & Order'}
                </button>
              </div>
            );
          })}
        </div>

        <Pagination />

      </div>
    </div>
  );
}